import { UserDetail } from '../../../js/api';

/**
 * Permission codes are `<action>__<app_label>__<model>` strings. Org reps carry
 * the codes their AuthGroups grant; staff carry the codes their `portal_*`
 * Django groups grant (a superuser gets every code). The staff user type only
 * opens the `/staff` route tree — access inside it is decided by these codes.
 *
 * Non-hook variant of `useHasPermission` for item builders and helpers that
 * run outside React rendering.
 */
export const hasPermission = (
  user: Pick<UserDetail, 'permissions'> | null | undefined,
  permission: string
): boolean => Boolean(user?.permissions?.includes(permission));

/** The `list__` code that opens each staff-portal menu (and its routes). */
export const STAFF_MENU_PERMISSIONS = {
  providers: 'list__providers__provider',
  needs: 'list__staffing__need',
  opportunities: 'list__assignments__opportunity',
  clearances: 'list__connections__clearance',
  connections: 'list__connections__connectedfacility',
  assignments: 'list__assignments__assignment',
  timecards: 'list__assignments__timecard',
  facilities: 'list__healthcare_entities__healthcarefacility',
  users: 'list__healthcare_entities__healthcareentityrep',
  imports: 'list__ingestion__ingestfile',
  bizintel: 'list__reports__report',
} as const;

/**
 * Staff sidenav / home-card visibility by item key. Admin rides on the Django
 * admin flag; BizIntel needs its portal code (per-report Django groups still
 * apply inside it); anything unlisted is always visible.
 */
export const STAFF_MENU_ITEM_PERMISSIONS: Record<string, string> = {
  providers: STAFF_MENU_PERMISSIONS.providers,
  needs: STAFF_MENU_PERMISSIONS.needs,
  opportunities: STAFF_MENU_PERMISSIONS.opportunities,
  clearance: STAFF_MENU_PERMISSIONS.clearances,
  connections: STAFF_MENU_PERMISSIONS.connections,
  assignments: STAFF_MENU_PERMISSIONS.assignments,
  timecards: STAFF_MENU_PERMISSIONS.timecards,
  facilities: STAFF_MENU_PERMISSIONS.facilities,
  users: STAFF_MENU_PERMISSIONS.users,
  imports: STAFF_MENU_PERMISSIONS.imports,
  bizintel: STAFF_MENU_PERMISSIONS.bizintel,
};

export const isStaffMenuItemVisible = (
  user: Pick<UserDetail, 'permissions' | 'is_staff'> | null | undefined,
  key: string
): boolean => {
  if (key === 'admin') return Boolean(user?.is_staff);
  const permission = STAFF_MENU_ITEM_PERMISSIONS[key];
  return !permission || hasPermission(user, permission);
};

// Staff-only capabilities (seeded only into Django auth, see
// backend accounts/services/portal_auth_service.py).
export const INGEST_FILE_CREATE_PERMISSION = 'create__ingestion__ingestfile';
export const INGEST_FILE_UPDATE_PERMISSION = 'update__ingestion__ingestfile';
export const INGEST_FILE_DELETE_PERMISSION = 'delete__ingestion__ingestfile';
export const INGEST_ROW_UPDATE_PERMISSION = 'update__ingestion__ingestrow';
export const SIGNATURE_REQUEST_UPDATE_PERMISSION = 'update__signatures__signaturerequest';

// Shared codes reused by staff-portal gates.
export const ASSIGNMENT_UPDATE_PERMISSION = 'update__assignments__assignment';
export const TIMECARD_READ_PERMISSION = 'read__assignments__timecard';
export const FACILITY_UPDATE_PERMISSION = 'update__healthcare_entities__healthcarefacility';
// Detail pages open on read alone; the menus/list pages need list__.
export const FACILITY_READ_PERMISSION = 'read__healthcare_entities__healthcarefacility';
